import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Snackbar,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

import {
  useListUsers,
  useUpdateOrganisationMembership,
} from "../../../../store";

interface AddMemberModalProps {
  open: boolean;
  teamId: string;
  onClose: () => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  teamId,
  onClose,
}: AddMemberModalProps) => {
  const [inProgress, setInProgress] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const { data: clientUsers = [] } = useListUsers();

  const availableUsers = useMemo(
    () => clientUsers.filter((user) => !user.organisationIds.includes(teamId)),
    [teamId, clientUsers],
  );

  const formik = useFormik({
    initialValues: {
      userId: "",
    },
    validationSchema: Yup.object({
      userId: Yup.string().required("Please select a user"),
    }),
    onSubmit: (values) => {
      const selectedUser = availableUsers.find(
        (user) => user.userId === values.userId,
      );

      if (!selectedUser) {
        return;
      }

      setInProgress(true);
      changeMembership({
        organisationId: teamId,
        input: {
          targetId: selectedUser.userId,
          membership: "true",
        },
      });
    },
  });

  const onMembershipSuccess = () => {
    setSnackbar({
      open: true,
      message: "User added to team",
      severity: "success",
    });
    setInProgress(false);
    onClose();
  };

  const onMembershipError = (err: unknown) => {
    console.error(err);
    setSnackbar({
      open: true,
      message: "Failed to add user to team",
      severity: "error",
    });
    setInProgress(false);
    onClose();
  };

  const { mutate: changeMembership } = useUpdateOrganisationMembership(
    onMembershipSuccess,
    onMembershipError,
  );

  const cancel = () => {
    formik.resetForm();
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar((current) => ({
      ...current,
      open: false,
    }));
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h3">Add new team member</Typography>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={formik.handleSubmit}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {availableUsers.length === 0 ? (
                <Typography color="text.secondary">
                  There are no users in this client who are not already part of
                  this team.
                </Typography>
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="add-user-to-org-label">User</InputLabel>
                  <Select
                    labelId="add-user-to-org-label"
                    id="add-user-to-org"
                    value={formik.values.userId}
                    label="User"
                    onChange={(event) => {
                      formik.setFieldTouched("userId", true);
                      formik.setFieldValue("userId", event.target.value);
                    }}
                  >
                    {availableUsers.map((user) => {
                      const displayName =
                        user.fullName || user.email || "Unknown user";
                      const initials =
                        displayName
                          .split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part[0]?.toUpperCase() || "")
                          .join("") || "U";

                      return (
                        <MenuItem key={user.userId} value={user.userId}>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{ width: "100%" }}
                          >
                            <Avatar
                              sx={{
                                width: 28,
                                height: 28,
                                fontSize: "0.75rem",
                                color: "primary.contrastText",
                                bgcolor: "primary.main",
                              }}
                            >
                              {initials}
                            </Avatar>
                            <ListItemText
                              primary={displayName}
                              secondary={user.email}
                            />
                          </Stack>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {formik.touched.userId && formik.errors.userId && (
                <Typography color="error">{formik.errors.userId}</Typography>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Button fullWidth variant="outlined" onClick={cancel}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={
                    inProgress ||
                    availableUsers.length === 0 ||
                    !formik.values.userId
                  }
                >
                  {inProgress ? "Loading..." : "Add User"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddMemberModal;
